import { useRef, useState } from 'react';
import { GenerateProductVariantWeightsAndDimensionsResponseDto } from '@tiendanube/common';
import { AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import { useToast, useTranslationCommon } from 'commons/hooks';
import { logError } from 'commons/logger';
import { useAiUsage } from 'domains/Ai/hooks';
import { trackingAiGenerateManualFieldEdit } from 'domains/Ai/tracking';
import { useGetLanguage } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useGenerateProductVariantWeightsAndDimensions } from 'domains/Catalog/Products/pages/hooks';
import useGenerateProduct from '../../../hooks/useGenerateProduct/useGenerateProduct';
import { RequiredAiProperties } from '../../Variants/types';
import { SizeField } from '../ProductSizes';

interface UseProductSizeWithAIProps {
  weight: string;
  depth: string;
  width: string;
  height: string;
  onChange: (data: { name: SizeField; value: string }) => void;
  requiredAiProperties: RequiredAiProperties;
  asAside: boolean;
  onWeightsAndDimensionsGenerated?: (
    response: GenerateProductVariantWeightsAndDimensionsResponseDto,
  ) => void;
}

type AiPopulatedFields = Record<SizeField, boolean>;

const WEIGHT_DECIMALS = 3;
const DIMENSION_DECIMALS = 2;

function useProductSizeWithAI({
  weight,
  depth,
  width,
  height,
  onChange,
  requiredAiProperties,
  asAside,
  onWeightsAndDimensionsGenerated,
}: UseProductSizeWithAIProps) {
  const tCommon = useTranslationCommon();
  const defaultLanguage = useGetLanguage();

  const { addToast } = useToast();
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const { generatedAIProductData } = useGenerateProduct();
  const hasGeneratedAIProductData = !!generatedAIProductData;
  const [aiPopulatedFields, setAiPopulatedFields] = useState<AiPopulatedFields>(
    {
      weight: hasGeneratedAIProductData,
      depth: hasGeneratedAIProductData,
      width: hasGeneratedAIProductData,
      height: hasGeneratedAIProductData,
    },
  );
  const aiGeneratedValuesRef = useRef<Record<SizeField, string>>({
    weight: generatedAIProductData?.weight_and_dimensions.weight ?? '',
    depth: generatedAIProductData?.weight_and_dimensions.depth ?? '',
    width: generatedAIProductData?.weight_and_dimensions.width ?? '',
    height: generatedAIProductData?.weight_and_dimensions.height ?? '',
  });

  const { generateWeightsAndDimensions } =
    useGenerateProductVariantWeightsAndDimensions();

  // AI usage and billing upsell functionality
  const { requiredTier, aiUsesUpsellTitle, refetchAiUses } = useAiUsage();

  const trackingFeatureProps = {
    feature: AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS,
  } as const;

  const handleFieldManualEdit = (name: SizeField, updatedValue: string) => {
    if (!aiPopulatedFields[name]) return;

    const originalValue = aiGeneratedValuesRef.current[name];
    const formattedUpdatedValue = updatedValue.trim();

    if (originalValue && originalValue !== formattedUpdatedValue) {
      trackingAiGenerateManualFieldEdit(name, trackingFeatureProps);
    }

    aiGeneratedValuesRef.current[name] = '';
    setAiPopulatedFields((prev) => ({ ...prev, [name]: false }));
  };

  const generateWeightsAndDimensionsWithAI = async () => {
    if (isGeneratingAI || !requiredAiProperties?.imageUrl) return;

    setIsGeneratingAI(true);

    try {
      const requestData = {
        product_image_url: requiredAiProperties.imageUrl,
        product_name: requiredAiProperties.name?.[defaultLanguage] || '',
        product_description:
          requiredAiProperties.description?.[defaultLanguage] || '',
        product_variant: requiredAiProperties.variantName || '',
        lang: defaultLanguage,
      };

      const response = await generateWeightsAndDimensions(requestData);

      const shouldUpdate = (currentValue: string) => {
        if (!currentValue || currentValue.trim() === '') return true;
        const numValue = parseFloat(currentValue);
        return numValue === 0;
      };

      const fieldsToUpdate: ReadonlyArray<{
        name: SizeField;
        current: string;
        aiValue: string;
      }> = [
        { name: 'weight', current: weight, aiValue: response.weight },
        { name: 'depth', current: depth, aiValue: response.depth },
        { name: 'width', current: width, aiValue: response.width },
        { name: 'height', current: height, aiValue: response.height },
      ];

      const updatedFields = new Set<SizeField>();
      const updatedValues: Partial<Record<SizeField, string>> = {};

      fieldsToUpdate.forEach(({ name, current, aiValue }) => {
        if (shouldUpdate(current)) {
          const parsedValue = parseFloat(aiValue);
          if (parsedValue > 0) {
            const formattedValue =
              name === 'weight'
                ? parsedValue.toFixed(WEIGHT_DECIMALS)
                : parsedValue.toFixed(DIMENSION_DECIMALS);
            onChange({ name, value: formattedValue });
            updatedFields.add(name);
            updatedValues[name] = formattedValue;
          }
        }
      });

      if (updatedFields.size > 0) {
        setAiPopulatedFields((prev) => {
          const newState = { ...prev };
          updatedFields.forEach((fieldName) => {
            newState[fieldName] = true;
            aiGeneratedValuesRef.current[fieldName] =
              updatedValues[fieldName] ?? '';
          });
          return newState;
        });
      }
      if (onWeightsAndDimensionsGenerated) {
        onWeightsAndDimensionsGenerated(response);
      }
      refetchAiUses();
      addToast({
        appearance: 'success',
        label: tCommon('aiGeneration.weightsAndDimensionsGenerated'),
      });
    } catch (error) {
      logError('Error generating weights and dimensions with AI', error);

      addToast({
        appearance: 'danger',
        label: tCommon('aiGeneration.errorGenerating'),
      });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleGenerateWithAI = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_WEIGHTS_AND_DIMENSIONS,
    callback: generateWeightsAndDimensionsWithAI,
    minValue: requiredTier,
    asAside,
  });

  const hasFieldValue = (value: string): boolean => {
    if (!value || value.trim() === '') return false;
    const numValue = parseFloat(value);
    return !Number.isNaN(numValue) && numValue !== 0;
  };

  const hasExistingDimensions =
    hasFieldValue(weight) &&
    hasFieldValue(depth) &&
    hasFieldValue(width) &&
    hasFieldValue(height);

  const isAIGeneratedField = (fieldName: SizeField): boolean =>
    !!aiPopulatedFields[fieldName];

  const getTooltipText = (): string | undefined => {
    if (!requiredAiProperties?.imageUrl) {
      return tCommon('aiGeneration.addPhotoToGenerate');
    }
    if (hasExistingDimensions) {
      return tCommon('aiGeneration.dimensionsAlreadyExist');
    }
    return undefined;
  };

  return {
    isGeneratingAI,
    handleFieldManualEdit,
    handleGenerateWithAI,
    hasExistingDimensions,
    isAIGeneratedField,
    getTooltipText,
  };
}

export default useProductSizeWithAI;
